import React, { useState, useEffect } from "react";
import { API_URL } from "../config";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Notification from "../components/Notification.js";

function PrayerCenterBookingView() {
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(false);
    const { bookingToken } = useParams();
    const [showBackButton, setShowBackButton] = useState(false);
    const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [broadcastToken, setBroadcastToken] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });

    useEffect(() => {
        const fetchBookingDetails = async () => {
            setLoading(true);
            setError(null); // Clear any previous errors
            setBooking(null); // Reset booking to null on new fetch

            try {
                const response = await fetch(`${API_URL}/bookings/token/${bookingToken}`);

                if (response.ok) {
                    const data = await response.json();
                    if (!data) {
                        throw new Error("No booking details found.");
                    }
                    setBooking(data);
                } else if (response.status === 403) {
                    const errorResponse = await response.json();
                    setShowBackButton(true);
                    throw new Error(errorResponse.message)
                }
            } catch (err) {
                setNotification({ type: "danger", message: err.message })
            } finally {
                setLoading(false);
            }
        };

        fetchBookingDetails();
    }, [bookingToken]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous messages
        setError("");
        setSuccessMessage("");

        // Validate phone number
        const strippedNumber = mpesaPhoneNumber.startsWith("0")
            ? mpesaPhoneNumber.slice(1)
            : mpesaPhoneNumber;

        if (!/^[1-9][0-9]{8}$/.test(strippedNumber)) {
            setError("Invalid phone number.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/bookings/pay`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    phone_number: strippedNumber,
                    booking_id: booking.id,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setLoading(true);
                setBroadcastToken(data.broadcastToken);

                setSuccessMessage(data.message);
            } else {
                const errorData = await response.json();
                setError(errorData.phone_number || "An error occurred while processing your request.");
                setNotification({ type: "danger", message: "There is an error in the form." });
            }
        } catch (err) {
            setError("An unexpected error.");
            setNotification({ type: "danger", message: "An unexpected error occurred. Please try again later." });
        }
    };

    const checkPaymentStatus = async (
        broadcastToken,
        setSuccessMessage,
        setError,
        startTime = Date.now()
    ) => {
        setLoading(true);
        // Calculate elapsed time
        const elapsed = Date.now() - startTime;

        // Stop polling if 1 minute (60,000 ms) has passed
        if (elapsed > 60000) {
            setNotification({ type: "danger", message: "Payment verification timed out. Please try again" });
            return;
        }

        try {
            const response = await fetch(`${API_URL}/bookings/${booking.id}/payment-status`);
            const data = await response.json();

            if (data.status === "success") {
                setNotification({ type: "success", message: data.message || "Payment successful!" });

                setTimeout(() => {
                    setBooking(data.booking);
                }, 2000);

            } else {
                // Try again after 5 seconds
                setTimeout(() => {
                    checkPaymentStatus(broadcastToken, setSuccessMessage, setError, startTime);
                }, 5000);
            }
        } catch (err) {
            setNotification({ type: "danger", message: "Error checking payment status" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (broadcastToken) {
            setTimeout(() => {
                checkPaymentStatus(broadcastToken, setSuccessMessage, setError);
            }, 3000);
        }
    }, [broadcastToken]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <div className="cf-public">
            <Header />
            <main className="cf-sec cf-pp">
                <div className="cf-wrap cf-narrow">
                    <nav className="cf-crumbs" aria-label="You are here">
                        <Link to="/makuyu-prayer-center-booking">Prayer center booking</Link>
                        <span aria-hidden="true">/</span>
                        <span>Your booking</span>
                    </nav>

                    {notification.message && (
                        <Notification
                            type={notification.type}
                            message={notification.message}
                            onClose={closeNotification}
                        />
                    )}

                    {showBackButton && (
                        <p className="mb-5">
                            <Link to="/makuyu-prayer-center-booking" className="cf-btn cf-btn-ghost">
                                <i className="fas fa-arrow-left" aria-hidden="true"></i> Try Again
                            </Link>
                        </p>
                    )}



                    {(loading && !booking) && <p className="cf-empty">Loading booking details...</p>}
                    {booking ? (
                        <div className="cf-card">
                            <h1 className="cf-card-title">Your booking</h1>
                            <div className="columns is-multiline">
                                {/* Payment Details Section */}



                                {!booking.payment_id && (
                                    <div className="column is-full">
                                    <div className="cf-callout cf-pay-box">
                                        <p>
                                            Please note that booking cannot be reserved for you until you make payment.
                                            <br />
                                            Pay <strong>Kes ###</strong> to reserve your space.
                                            <br />
                                            Your Payment option is <strong>Mpesa Express</strong>.
                                        </p>

                                        <div className="columns is-multiline">
                                            {/* Payment Instructions */}
                                            <div className="column is-half">
                                                <p className="mt-3">
                                                    Click on the "Make Payment" button to send a payment request to your phone.
                                                    A popup will appear on your phone. Enter your MPESA PIN. You will get a
                                                    confirmation SMS and an email from us.
                                                </p>
                                            </div>

                                            {/* Payment Form */}
                                            <div className="column is-half mt-0 pt-0">
                                                <div>
                                                    <form onSubmit={handleSubmit}>
                                                        <div className="field">
                                                            <label className="label">Mpesa Phone Number</label>
                                                            <div className="control is-flex has-addons">
                                                                <span className="button is-static">+254</span>
                                                                <input
                                                                    className={`input ${error ? "is-danger" : ""}`}
                                                                    type="text"
                                                                    placeholder="Enter your phone number"
                                                                    value={mpesaPhoneNumber}
                                                                    onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                                                                    required
                                                                    disabled={loading}
                                                                />
                                                            </div>
                                                        </div>

                                                        {error && <p className="help is-danger">{error}</p>}
                                                        {successMessage && <p className="help is-success">{successMessage}</p>}


                                                        <div className="field">
                                                            <div className="control">
                                                                <button
                                                                    className={`button is-primary ${loading ? "is-loading" : ""}`}
                                                                    type="submit"
                                                                    disabled={loading}
                                                                >
                                                                    Make Payment
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                )}

                                {/* Reference Number */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Reference Number</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.reference_no}</p>
                                    </div>
                                </div>

                                {/* Booking Type */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Booking Type</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.booking_type}</p>
                                    </div>
                                </div>

                                {/* Individual Name */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Your Name</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.individual_name}</p>
                                    </div>
                                </div>

                                {/* Group Name */}
                                {booking.booking_type === "Group" && (
                                    <div className="column is-half">
                                        <div className="field">
                                            <label className="label">Group Name</label>
                                            <p className="control is-expanded has-background-light p-2">{booking.group_name}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Start Date */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Start Date</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.start_date}</p>
                                    </div>
                                </div>

                                {/* End Date */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">End Date</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.end_date}</p>
                                    </div>
                                </div>

                                {/* Number of People */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Number of People</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.number_of_people}</p>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Email</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.email}</p>
                                    </div>
                                </div>

                                {/* Phone Number */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Phone Number</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.phone_number}</p>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Status</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.status}</p>
                                    </div>
                                </div>

                                {/* Payment Status */}
                                <div className="column is-half">
                                    <div className="field">
                                        <label className="label">Payment Status</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.payment_id ? 'PAID' : 'NOT PAID'}</p>
                                    </div>
                                </div>

                                {/* Comment */}
                                {booking.comment && (
                                    <div className="column is-half">
                                        <div className="field">
                                            <label className="label">Remarks</label>
                                            <p className="control is-expanded has-background-light p-2">{booking.comment}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Special Requests */}
                                <div className="column is-full">
                                    <div className="field">
                                        <label className="label">Special Requests</label>
                                        <p className="control is-expanded has-background-light p-2">{booking.special_requests || "None"}</p>
                                    </div>
                                </div>

                                {/* Optional Fields */}
                                {booking.amount_paid && (
                                    <div className="column is-half">
                                        <div className="field">
                                            <label className="label">Amount Paid</label>
                                            <p className="control is-expanded has-background-light p-2">{booking.amount_paid}</p>
                                        </div>
                                    </div>
                                )}

                                {booking.payment_reference && (
                                    <div className="column is-half">
                                        <div className="field">
                                            <label className="label">Payment Reference</label>
                                            <p className="control is-expanded has-background-light p-2">{booking.payment_reference}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        !error && !loading && <p className="cf-empty">No booking details available.</p>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default PrayerCenterBookingView;
